import { Box, Flex, Grid, Heading, Spacer, Switch } from '@chakra-ui/react';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Mspr from '../utils/Mspr';
import { Bank, MotorSynthPresets, Preset } from '../utils/types';
import { BankCard } from './BankCard';
import { DraggableType, DropZone } from './DropZone';
import { PresetCard } from './PresetCard';

interface Props {
  msPresets: MotorSynthPresets;
  handleRename: (entity: Bank | Preset, newName: string) => void;
  handleBankDrop: (item: Bank, targetIndex: number) => void;
  handlePresetDrop: (item: Preset, targetBankId: string, targetIndex: number) => void;
}

export const FileTree = ({ msPresets, handleRename, handleBankDrop, handlePresetDrop }: Props) => {
  const { t } = useTranslation();
  const { banks } = msPresets;
  const [hideDraggedIndex, _] = useState<string | undefined>(undefined);
  const [selectedBank, setSelectedBank] = useState<Bank | undefined>(banks.find(bank => !!bank.name));
  const [selectedBankIndex, setSelectedBankIndex] = useState<number>(banks.findIndex(bank => !!bank.name));
  const [hideEmptyBanks, setHideEmptyBanks] = useState<boolean>(false);
  // is any drag operation in progress
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const ref = createRef<HTMLDivElement>();

  useEffect(() => {
    if (selectedBank) {
      const bank = banks.find(bank => bank.uniqueId === selectedBank.uniqueId);
      if (bank) {
        setSelectedBank(bank);
      }
    }
  }, [msPresets]);

  useEffect(() => {
    // set selectedbankindex
    if (selectedBank) {
      const index = banks.findIndex(bank => bank.uniqueId === selectedBank.uniqueId);
      setSelectedBankIndex(index);
    }
  }, [selectedBank]);

  const indexOfLastNonEmptyBank = banks
    .map((bank, index) => ({ bank, index }))
    .filter(({ bank }) => !Mspr.isEmpty(bank))
    .map(({ index }) => index)
    .pop();

  // Scroll into view when file tree is created
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // consecutiveIndices - each bank and preset has its own index, that are consecutive
  return (<>
    <Flex
      width='100%'
      color='#fff'
      gap='0.5rem'
    >
      <Switch
        isChecked={hideEmptyBanks}
        onChange={() => setHideEmptyBanks(!hideEmptyBanks)}
        marginLeft={{ base: 0, md: '1rem' }}
        colorScheme='primary'
      >
        {t('hideEmptyBanks')}
      </Switch>
      <Spacer />
    </Flex>
    <Box
      width='100%'
      style={{
        fontFamily: 'Roboto Mono, monospace',
        fontSize: '0.8em',
      }}
      ref={ref}
    >
      <Grid
        templateColumns='1fr 1fr'
        paddingX='1rem'
        columnGap='1rem'
        rowGap='0.5rem'
        marginBottom='2rem'
      >
        {/* Bank column */}
        <Heading size='md' color='#fff'>
          {t('banks')}
        </Heading>
        {selectedBank &&
          <Heading size='md' color='#fff' >
            [B{selectedBankIndex}] {selectedBank?.name || '<UNTITLED>'}
          </Heading>
        }
        <Flex
          direction='column'
          maxHeight='80vh'
          overflow='auto'
          paddingRight='1rem'
        >
          {banks.map((bank, index) => ({ bank, index }))
            .filter(({ bank }) => !(hideEmptyBanks && Mspr.isEmpty(bank)))
            .map(({ bank, index }) => <React.Fragment key={bank.uniqueId}>
              <DropZone
                onDrop={(item, targetIndex) => handleBankDrop(item as Bank, targetIndex)}
                accept={[DraggableType.BANK]}
                index={index - 1}
                marginY='0.25rem'
                minHeight='1rem'
                maxHeight='4rem'
              />
              <BankCard
                bank={bank}
                index={index}
                hideIndices={hideDraggedIndex === bank.uniqueId.toString()}
                handleRename={handleRename}
                handlePresetDrop={handlePresetDrop}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={() => setIsDragging(false)}
                onBankSelect={() => setSelectedBank(bank)}
                isDragging={isDragging}
                isActive={selectedBank?.uniqueId === bank.uniqueId}
              />
              {/* HACK: Find the last non-empty bank index */}
              {index === (hideEmptyBanks
                ? indexOfLastNonEmptyBank
                : banks.length - 1) && <DropZone
                  onDrop={(item, targetIndex) => handleBankDrop(item as Bank, targetIndex)}
                  accept={[DraggableType.BANK]}
                  index={index}
                  marginY='0.25rem'
                  minHeight='1rem'
                  maxHeight='4rem'
                />}
            </React.Fragment>
            )}
        </Flex>

        {/* Preset column */}
        <Flex
          direction='column'
          overflow='auto'
          maxHeight='80vh'
          paddingRight='1rem'
        >
          {selectedBank && selectedBank.presets.filter(p => !!p).map((preset, index) => (
            <React.Fragment key={preset!.uniqueId}>
              <DropZone
                onDrop={(item, targetIndex) => handlePresetDrop(item as Preset, selectedBank.uniqueId, targetIndex)}
                accept={[DraggableType.PRESET]}
                index={index - 1}
                maxHeight='3rem'
                minHeight='1rem'
              />
              <PresetCard
                preset={preset}
                index={index}
                hideIndices={false}
                handleRename={handleRename}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={() => setIsDragging(false)}
              />
              {index === selectedBank.presets.filter(p => !!p).length - 1 && <DropZone
                onDrop={(item, targetIndex) => handlePresetDrop(item as Preset, selectedBank.uniqueId, targetIndex)}
                accept={[DraggableType.PRESET]}
                index={index}
                maxHeight='3rem'
                minHeight='1rem'
              />}
            </React.Fragment>
          ))}
        </Flex>
      </Grid>
    </Box>
  </>
  );
};
